import type { WorkflowNode } from '../../../types/backend';

// How the stages of a workflow connect: where a run can go from here, who can reach here, and
// what nothing reaches. The model's own questions, with no geometry in them, which is why they
// are not in workflowLayout beside the arithmetic that draws the answers.

export interface Route {
  condition: string | null;
  target: number | null;
  decisionId: string | null;
}

export const byLayerOf = (nodes: WorkflowNode[]): Map<number, WorkflowNode[]> => {
  const map = new Map<number, WorkflowNode[]>();
  nodes.forEach((node) => map.set(node.layer, [...(map.get(node.layer) ?? []), node]));
  return map;
};

export const stagesOf = (nodes: WorkflowNode[]): number[] =>
  [...new Set(nodes.map((n) => n.layer))].sort((a, b) => a - b);

/** Where a run can go when this stage is done. A target of null means the workflow ends. */
export const routesFrom = (layer: number, nodes: WorkflowNode[]): Route[] => {
  const byLayer = byLayerOf(nodes);
  const members = byLayer.get(layer) ?? [];
  const decision = members.find((n) => n.kind === 'decision');
  if (decision) {
    return (decision.branches ?? []).map((b) => ({
      condition: b.condition || 'unnamed',
      target: byLayer.has(b.target_layer) ? b.target_layer : null,
      decisionId: decision.id,
    }));
  }
  const next = stagesOf(nodes).filter((l) => l > layer)[0];
  return [{ condition: null, target: next ?? null, decisionId: null }];
};

/** Who can send a run here. */
export const routesInto = (layer: number, nodes: WorkflowNode[]): (Route & { from: number })[] =>
  stagesOf(nodes)
    .filter((l) => l !== layer)
    .flatMap((from) =>
      routesFrom(from, nodes)
        .filter((r) => r.target === layer)
        .map((r) => ({ ...r, from })),
    );

export const reachableStages = (nodes: WorkflowNode[]): Set<number> => {
  const stages = stagesOf(nodes);
  const seen = new Set<number>();
  const stack = stages.length > 0 ? [stages[0]] : [];
  while (stack.length > 0) {
    const layer = stack.pop() as number;
    if (seen.has(layer)) continue;
    seen.add(layer);
    routesFrom(layer, nodes).forEach((r) => {
      if (r.target !== null) stack.push(r.target);
    });
  }
  return seen;
};

/** True when the run simply falls through to the stage drawn directly below. */
export const fallsThroughTo = (layer: number, nodes: WorkflowNode[]): number | null => {
  const routes = routesFrom(layer, nodes);
  if (routes.length !== 1 || routes[0].condition !== null) return null;
  const next = stagesOf(nodes).filter((l) => l > layer)[0];
  return routes[0].target === next ? next : null;
};
